import React from 'react';
import { Wallet, Laptop, Translate, CaretRight, MapPin, TrendUp, TrendDown } from '@phosphor-icons/react';
import { Link } from './Link';
import { buildLocationSrpPath } from '../utils/location/build-location-srp-path';
import type { MarketHeaderData } from '../hooks/useMarketHeaderData';
import { Msg } from '../i18n-review/client/Msg';
import { HubCrossLinks } from './hub/HubCrossLinks';
import type { HubCrossLink } from '../types/hub-cross-link';
import { hubLinksHeading } from '../utils/seo/hub-index-links';

export interface MarketStatCardsProps {
  data: MarketHeaderData;
  hubLinks?: HubCrossLink[];
  className?: string;
}

/** Same icon-badge accent palette as MarketRail. */
const ACCENTS = {
  salary: { badgeBg: 'bg-[#F5EDD0] dark:bg-[#2A1E08]', icon: 'text-[#6E4F00] dark:text-[#D4A820]' },
  workModel: { badgeBg: 'bg-[#C8DDCE] dark:bg-[#1A2E22]', icon: 'text-[#3B5E45] dark:text-[#98CFA8]' },
  language: { badgeBg: 'bg-[#DDD5C8] dark:bg-[#2A1D0F]', icon: 'text-[#5A3E20] dark:text-[#D4B898]' },
};

function TrendGlyph({ up }: { up: boolean }): React.ReactElement {
  return up ? (
    <TrendUp size={10} weight="bold" className="text-[#3B5E45] dark:text-[#98CFA8]" />
  ) : (
    <TrendDown size={10} weight="bold" className="text-[#6E4F00] dark:text-[#D4A820]" />
  );
}

function MiniStatCard({
  accent,
  icon,
  label,
  trendUp,
  value,
}: {
  accent: keyof typeof ACCENTS;
  icon: React.ReactNode;
  label: React.ReactNode;
  trendUp?: boolean;
  value: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="snap-start shrink-0 w-[calc(33.333%-0.5rem)] min-w-[7.5rem] rounded-2xl border border-[#DDD5C8] dark:border-[#4A3520] bg-white dark:bg-[#1F1509] px-3 py-2.5">
      <div className="flex items-center justify-between gap-1">
        <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${ACCENTS[accent].badgeBg} ${ACCENTS[accent].icon}`}>
          {icon}
        </span>
        {trendUp !== undefined && <TrendGlyph up={trendUp} />}
      </div>
      <div className="mt-1.5 text-[9.5px] font-semibold uppercase tracking-wide text-[#7A6E5F] dark:text-[#B8A48C] truncate">{label}</div>
      <div className="mt-0.5 text-base font-bold tracking-tight text-[#1E1A14] dark:text-[#F2EADF]">{value}</div>
    </div>
  );
}

/**
 * Mobile presentation: a full-width photo hero (location name + open-job count over the image,
 * same visual language as MarketRail's desktop card header) that expands downward on tap to
 * reveal the stat cards, prose, blurbs, and nearby-city links. Collapsed content stays in the DOM
 * (curl the SSR HTML to confirm — see Guardrails in the implementation plan): this is a CSS-only
 * max-height/opacity toggle, not conditional rendering, specifically so the location's market text
 * is always present in the server-rendered HTML for crawlers regardless of the open/closed state a
 * given visitor leaves it in.
 */
export function MarketStatCards({ data, hubLinks, className = '' }: MarketStatCardsProps): React.ReactElement {
  const {
    intl,
    formatMessage,
    locale,
    effectiveBackgroundImage,
    backgroundImageAlt,
    resultsHeadingText,
    translatedLocation,
    sampleCount,
    description,
    locationHeaderText,
    salaryValue,
    finlandAverages,
    avgSalaryNum,
    workModelData,
    languageData,
    remotePercentage,
    nearbyLocations,
    handleRemoveLocation,
  } = data;

  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [backgroundImageLoadFailed, setBackgroundImageLoadFailed] = React.useState(false);

  React.useEffect(() => {
    setBackgroundImageLoadFailed(false);
  }, [effectiveBackgroundImage?.src]);

  const isShowingBackgroundImage = Boolean(effectiveBackgroundImage && !backgroundImageLoadFailed);
  const englishPercent = languageData.total > 0 ? Math.round((languageData.english / languageData.total) * 100) : null;
  const primaryWorkModelPercent =
    workModelData.total > 0
      ? Math.max(workModelData.onsitePercent ?? 0, workModelData.hybridPercent ?? 0, workModelData.remotePercent ?? 0)
      : null;
  const teaser = description && !locationHeaderText.overviewText ? description : locationHeaderText.overviewText;

  return (
    <div className={`lg:hidden ${className}`} aria-labelledby="location-header-title-cards">
      {/* Photo hero — the whole card is the expand/collapse control, same "text over bottom
          gradient" legibility approach as MarketRail's desktop header. */}
      <button
        type="button"
        onClick={() => setDetailsOpen((v) => !v)}
        className="relative w-full h-36 rounded-[1.75rem] overflow-hidden text-left cursor-pointer"
        aria-expanded={detailsOpen}
        aria-controls="market-stat-cards-details"
        aria-label={formatMessage({
          id: 'marketHeader.toggle',
          defaultMessage: 'Toggle market header',
          description: 'Button aria-label to show/hide market statistics header section',
        })}
      >
        {isShowingBackgroundImage && effectiveBackgroundImage ? (
          <img
            src={effectiveBackgroundImage.src}
            srcSet={effectiveBackgroundImage.srcset || undefined}
            sizes="100vw"
            alt={backgroundImageAlt}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            onError={() => setBackgroundImageLoadFailed(true)}
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[#3B5E45] via-[#2F4D3A] to-[#1E1A14] dark:from-[#22322A] dark:via-[#1A2620] dark:to-[#170F07]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/15 to-black/0" />
        <div className="absolute inset-0 flex flex-col justify-end p-4">
          <div className="flex items-center gap-1 mb-0.5">
            <MapPin size={12} weight="fill" className="text-white/70" />
            <span className="text-[10px] font-semibold uppercase tracking-wide text-white/70">{translatedLocation}</span>
          </div>
          <p id="location-header-title-cards" aria-hidden="true" className="text-xl font-bold tracking-tight leading-tight text-white line-clamp-2">
            {resultsHeadingText}
          </p>
          {sampleCount > 0 && (
            <p className="text-xs mt-0.5 text-white/80">
              <Msg
                id="marketHeader.rail.samplesUpdatedDaily"
                defaultMessage="{count} open jobs · updated daily"
                values={{ count: intl.formatNumber(sampleCount) }}
                description="Rail card subtitle: sample count and freshness cadence under the location title"
              />
            </p>
          )}
        </div>
        <span className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center bg-white/15 backdrop-blur-sm text-white">
          <CaretRight size={16} weight="bold" className={`transition-transform duration-300 ${detailsOpen ? 'rotate-90' : ''}`} />
        </span>
      </button>

      {/* Always-visible teaser (not just SEO-hidden) — one line of real prose before the reader opts into the full disclosure. */}
      {teaser && (
        <p className="mt-2.5 px-2 @sm:px-2 @md:px-4 text-xs leading-relaxed text-[#7A6E5F] dark:text-[#B8A48C] line-clamp-1">
          {teaser}
        </p>
      )}

      {/* Stat cards + prose + blurbs + nearby links — always in DOM (SEO), visually hidden until the hero image is tapped open. */}
      <div
        id="market-stat-cards-details"
        className={`overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out px-2 @sm:px-2 @md:px-4 ${
          detailsOpen ? 'max-h-[5000px] opacity-100 mt-2.5' : 'max-h-0 opacity-0 pointer-events-none'
        }`}
        aria-hidden={!detailsOpen}
      >
        <div className="rounded-[1.75rem] border border-[#DDD5C8] dark:border-[#4A3520] bg-white dark:bg-[#1F1509] shadow-[0_1px_2px_rgba(30,26,20,0.04),0_10px_28px_rgba(30,26,20,0.07)] dark:shadow-none p-4">
          <div className="flex gap-2 overflow-x-auto scrollbar-hide snap-x pb-0.5">
            <MiniStatCard
              accent="salary"
              icon={<Wallet size={11} weight="bold" />}
              label={<Msg id="marketHeader.salary.label" defaultMessage="Salary Trends" description="Label for salary trends card in market header" />}
              trendUp={
                finlandAverages?.avgSalaryNum && avgSalaryNum > 0 && avgSalaryNum !== finlandAverages.avgSalaryNum
                  ? avgSalaryNum > finlandAverages.avgSalaryNum
                  : undefined
              }
              value={salaryValue ?? '—'}
            />
            <MiniStatCard
              accent="workModel"
              icon={<Laptop size={11} weight="bold" />}
              label={<Msg id="marketHeader.workModel.label" defaultMessage="Work Model" description="Section label for work model statistics (remote, hybrid, onsite) in market header" />}
              trendUp={
                finlandAverages?.remotePercentage !== undefined && remotePercentage > 0 && remotePercentage !== finlandAverages.remotePercentage
                  ? remotePercentage > finlandAverages.remotePercentage
                  : undefined
              }
              value={primaryWorkModelPercent !== null ? `${Math.round(primaryWorkModelPercent)}%` : '—'}
            />
            <MiniStatCard
              accent="language"
              icon={<Translate size={11} weight="bold" />}
              label={<Msg id="marketHeader.language.label" defaultMessage="Language Demand" description="Section label for language requirement statistics in market header" />}
              trendUp={
                finlandAverages?.englishLanguagePercentage !== undefined && englishPercent !== null && englishPercent !== finlandAverages.englishLanguagePercentage
                  ? englishPercent > finlandAverages.englishLanguagePercentage
                  : undefined
              }
              value={englishPercent !== null ? `${englishPercent}%` : '—'}
            />
          </div>
          {teaser && (
            <p className="mt-2.5 text-sm leading-relaxed text-[#3D3529] dark:text-[#C4A882] mb-2">{teaser}</p>
          )}
          {locationHeaderText.salaryBlurb && (
            <p className="text-xs leading-relaxed text-[#7A6E5F] dark:text-[#B8A48C] mb-1.5">{locationHeaderText.salaryBlurb}</p>
          )}
          {locationHeaderText.workModelBlurb && (
            <p className="text-xs leading-relaxed text-[#7A6E5F] dark:text-[#B8A48C] mb-1.5">{locationHeaderText.workModelBlurb}</p>
          )}
          {locationHeaderText.languageBlurb && (
            <p className="text-xs leading-relaxed text-[#7A6E5F] dark:text-[#B8A48C] mb-1.5">{locationHeaderText.languageBlurb}</p>
          )}
          {nearbyLocations.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {nearbyLocations.map((city) => (
                <Link
                  key={city.slug}
                  href={buildLocationSrpPath(city.slug, locale)}
                  locale={locale}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-[#3B5E45] dark:text-[#98CFA8] bg-[#F2EADF] dark:bg-[#2A1D0F] rounded-full px-2.5 py-1"
                >
                  <MapPin size={10} weight="fill" className="opacity-60" />
                  {city.displayName}
                </Link>
              ))}
            </div>
          )}
          {hubLinks && hubLinks.length > 0 && (
            <HubCrossLinks
              variant="chips"
              links={hubLinks}
              locale={locale as 'en' | 'fi' | 'sv'}
              heading={hubLinksHeading(locale as 'en' | 'fi' | 'sv')}
              headingLevel="h3"
              className="mt-3"
            />
          )}
          <button
            type="button"
            onClick={handleRemoveLocation}
            className="mt-2 text-xs font-medium text-[#3B5E45] dark:text-[#98CFA8] cursor-pointer"
          >
            <Msg id="marketHeader.removeLocation" defaultMessage="Remove location" description="Button label to remove the active location filter" />
          </button>
        </div>
      </div>
    </div>
  );
}
